/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  IconButton
} from '@mui/material';
import { 
  FirstPage, 
  LastPage, 
  KeyboardArrowLeft, 
  KeyboardArrowRight,
  Search as SearchIcon 
} from '@mui/icons-material';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';
import useFacility from '../../hooks/useFacility';
import { toast } from 'react-toastify';

const PotManagement = () => {
  // Get admin's facility preference
  const { facilityPreference, loading: prefLoading } = useFacility();
  
  const [pots, setPots] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState('all');
  
  // Set facility filter from admin preference when loaded
  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPot, setEditingPot] = useState(null);
  const [newPot, setNewPot] = useState({
    pot: '',
    pot_description: '',
    facility: ''
  });

  // Pagination and search states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPots, setFilteredPots] = useState([]);

  // Sorting hook
  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredPots,
    { defaultSortColumn: 'pot', defaultSortOrder: 'asc' }
  );

  const fetchData = useCallback(async () => {
    try {
      const [potsRes, facilitiesRes] = await Promise.all([
        axios.get('/api/pots'),
        axios.get('/api/facilities')
      ]);
      
      let fetchedPots = potsRes.data;
      if (selectedFacility !== 'all') {
        fetchedPots = fetchedPots.filter(
          pot => pot.facility?._id === selectedFacility
        );
      }
      
      setPots(fetchedPots);
      setFilteredPots(fetchedPots);
      setFacilities(facilitiesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error details:', error);
      setError('Failed to fetch data');
      setLoading(false);
    }
  }, [selectedFacility]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const filtered = pots.filter(pot => 
      Object.values(pot).some(value => 
        value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
      ) ||
      pot.facility?.facility_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredPots(filtered);
    setPage(0);
  }, [searchQuery, pots]);

  // Calculate pagination
  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, sortedData.length);
  const paginatedPots = sortedData.slice(startIndex, endIndex);

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreate = async () => {
    try {
      if (!newPot.pot || !newPot.pot_description || !newPot.facility) {
        alert('Please fill in all required fields');
        return;
      }

      const potData = {
        pot: newPot.pot.trim(),
        pot_description: newPot.pot_description.trim(),
        facility: newPot.facility // This should be the facility ID
      };

      await axios.post('/api/pots', potData);
      await fetchData();
      toast.success('Pot created successfully');
      setNewPot({
        pot: '',
        pot_description: '',
        facility: ''
      });
    } catch (error) {
      console.error('Error creating pot:', error);
      toast.error(error.response?.data?.error || 'Failed to create pot');
    }
  };

  const handleUpdate = async () => {
    try {
      if (!editingPot.pot || !editingPot.pot_description || !editingPot.facility) {
        alert('Please fill in all required fields');
        return;
      }

      const potData = {
        pot: editingPot.pot.trim(),
        pot_description: editingPot.pot_description.trim(),
        facility: typeof editingPot.facility === 'object' ? editingPot.facility._id : editingPot.facility
      };

      await axios.put(`/api/pots/${editingPot._id}`, potData);
      await fetchData(); // Refresh the data to get updated list with populated facilities
      setEditDialogOpen(false);
      setEditingPot(null);
    } catch (error) {
      console.error('Error updating pot:', error);
      alert(error.response?.data?.error || 'Failed to update pot');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this pot?')) {
      try {
        await axios.delete(`/api/pots/${id}`);
        await fetchData(); // Refresh the data after deletion
      } catch (error) {
        console.error('Error deleting pot:', error);
        alert('Failed to delete pot');
      }
    }
  };

  const renderPaginationControls = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <span>Show</span>
        <Select
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          sx={{ mx: 1 }}
          size="small"
        >
          {[10, 25, 50, 100].map(n => (
            <MenuItem key={n} value={n}>{n}</MenuItem>
          ))}
        </Select>
        <span>entries</span>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton aria-label="First page"
          onClick={() => handleChangePage(0)}
          disabled={page === 0}
        >
          <FirstPage />
        </IconButton>
        <IconButton aria-label="Previous page"
          onClick={() => handleChangePage(page - 1)}
          disabled={page === 0}
        >
          <KeyboardArrowLeft />
        </IconButton>
        <Box sx={{ mx: 2 }}>
          Page {page + 1} of {Math.ceil(filteredPots.length / rowsPerPage)}
        </Box>
        <IconButton aria-label="Next page"
          onClick={() => handleChangePage(page + 1)}
          disabled={page >= Math.ceil(filteredPots.length / rowsPerPage) - 1}
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton aria-label="Last page"
          onClick={() => handleChangePage(Math.ceil(filteredPots.length / rowsPerPage) - 1)}
          disabled={page >= Math.ceil(filteredPots.length / rowsPerPage) - 1}
        >
          <LastPage />
        </IconButton>
      </Box>
    </Box>
  );

  const renderCreateDialog = () => (
    <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
      <DialogTitle>Add New Pot</DialogTitle>
      <DialogContent>
        <TextField
          label="Pot"
          fullWidth
          margin="normal"
          value={newPot.pot}
          onChange={(e) => setNewPot({...newPot, pot: e.target.value})}
        />
        <TextField
          label="Description"
          fullWidth
          margin="normal"
          value={newPot.pot_description}
          onChange={(e) => setNewPot({...newPot, pot_description: e.target.value})}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Facility</InputLabel>
          <Select
            value={newPot.facility}
            onChange={(e) => setNewPot({...newPot, facility: e.target.value})}
          >
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
      <DialogTitle>Edit Pot</DialogTitle>
      <DialogContent>
        <TextField
          label="Pot"
          fullWidth
          margin="normal"
          value={editingPot?.pot || ''}
          onChange={(e) => setEditingPot({...editingPot, pot: e.target.value})}
        />
        <TextField
          label="Description"
          fullWidth
          margin="normal"
          value={editingPot?.pot_description || ''}
          onChange={(e) => setEditingPot({...editingPot, pot_description: e.target.value})}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Facility</InputLabel>
          <Select
            value={editingPot?.facility?._id || ''}
            onChange={(e) => setEditingPot({
              ...editingPot,
              facility: e.target.value
            })}
          >
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleUpdate} variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="pot-management">
      <h2>Pot Management</h2>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search pots..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Facility</InputLabel>
          <Select
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
            label="Filter by Facility"
          >
            <MenuItem value="all">All Facilities</MenuItem>
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={() => setCreateDialogOpen(true)}
        sx={{ mb: 2 }}
      >
        Add New Pot
      </Button>

      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={resetSort}>Reset Sort</Button>
      </Box>

      <table className="data-table">
        <thead>
          <tr>
            <SortableTableHeader column="pot" label="Pot" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="pot_description" label="Description" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="facility.facility_name" label="Facility" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedPots.map(pot => (
            <tr key={pot._id}>
              <td>{pot.pot}</td>
              <td>{pot.pot_description}</td>
              <td>{pot.facility?.facility_name}</td>
              <td className="action-buttons">
                <Button
                  variant="contained"
                  size="small"
                  className="row-action-edit"
                  style={{ marginRight: '8px' }}
                  onClick={() => {
                    setEditingPot(pot);
                    setEditDialogOpen(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={() => handleDelete(pot._id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {renderPaginationControls()}
      {renderCreateDialog()}
      {renderEditDialog()}
    </div>
  );
};

export default PotManagement;